import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { VideoGrid } from '@/components/videos/video-grid'
import { Button } from '@/components/ui/button'
import { Plus, Search, TrendingUp, Filter, AlertCircle, Trash2 } from 'lucide-react'
import { useAuthStore } from '@/lib/store'
import { supabase } from '@/lib/supabase'
import { Toast } from '@/components/ui/toast'
import { BackButton } from '@/components/ui/back-button'

export function VideosPage() {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const [searchQuery, setSearchQuery] = useState('')
  const [sortBy, setSortBy] = useState<'recent' | 'popular'>('recent')
  const [myVideos, setMyVideos] = useState<any[]>([])
  const [showMyVideos, setShowMyVideos] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [videoToDelete, setVideoToDelete] = useState<string | null>(null)

  useEffect(() => {
    if (user && showMyVideos) {
      fetchMyVideos()
    }
  }, [user, showMyVideos])

  const fetchMyVideos = async () => {
    if (!user) return
    
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('videos')
        .select('*, profiles(full_name), products(title)')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })

      if (error) throw error
      setMyVideos(data || [])
    } catch (err) {
      console.error('Error fetching my videos:', err)
      setToastMessage('Failed to load your videos')
      setToastType('error')
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteVideo = async () => {
    if (!videoToDelete || !user) return

    try {
      setLoading(true)
      
      // Delete the video from the database
      const { error } = await supabase
        .from('videos')
        .delete()
        .eq('id', videoToDelete)
        .eq('user_id', user.id)

      if (error) throw error

      // Show success message
      setToastMessage('Video deleted successfully')
      setToastType('success')
      setShowToast(true)
      
      // Refresh the list
      fetchMyVideos()
    } catch (err) {
      console.error('Error deleting video:', err)
      setToastMessage('Failed to delete video')
      setToastType('error')
      setShowToast(true)
    } finally {
      setLoading(false)
      setShowDeleteConfirm(false)
      setVideoToDelete(null)
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            En attente d'approbation
          </span>
        )
      case 'approved':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Approuvée
          </span>
        )
      case 'rejected':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            Refusée
          </span>
        )
      default:
        return null
    }
  }

  return (
    <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      {showToast && (
        <Toast
          type={toastType}
          title={toastMessage}
          onClose={() => setShowToast(false)}
        />
      )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full m-4">
            <div className="flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mx-auto">
              <Trash2 className="h-6 w-6 text-red-600" />
            </div>
            <div className="mt-3 text-center">
              <h3 className="text-lg font-medium text-gray-900">
                Confirmer la suppression
              </h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  Êtes-vous sûr de vouloir supprimer cette vidéo ? Cette action est irréversible.
                </p>
              </div>
            </div>
            <div className="mt-5 flex justify-end space-x-3">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowDeleteConfirm(false)
                  setVideoToDelete(null)
                }}
              >
                Annuler
              </Button>
              <Button
                variant="outline"
                className="text-red-600 hover:text-red-700"
                onClick={handleDeleteVideo}
                disabled={loading}
              >
                {loading ? 'Suppression...' : 'Supprimer'}
              </Button>
            </div>
          </div>
        </div>
      )}
      
      <div className="mb-6">
        <BackButton to="/" label="Retour à l'accueil" />
      </div>
      
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {showMyVideos ? 'Mes Vidéos' : 'Vidéos Produits'}
          </h1>
          <p className="mt-2 text-sm text-gray-500">
            {showMyVideos 
              ? 'Gérez vos vidéos téléchargées' 
              : 'Regardez des démonstrations et avis de produits de notre communauté'}
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
          {user && (
            <Button
              variant="outline"
              onClick={() => {
                setShowMyVideos(!showMyVideos)
                if (!showMyVideos) fetchMyVideos()
              }}
              className="w-full sm:w-auto"
            >
              {showMyVideos ? 'Voir toutes les vidéos' : 'Mes vidéos'}
            </Button>
          )}
          {user && (
            <Button
              variant="primary"
              onClick={() => navigate('/videos/new')}
              className="w-full sm:w-auto"
            >
              <Plus className="h-5 w-5 mr-2" />
              Télécharger une vidéo
            </Button>
          )}
        </div>
      </div>

      {showMyVideos ? (
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4">Mes Vidéos</h2>
          
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : myVideos.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Vous n'avez pas encore téléchargé de vidéos</p>
              <Button
                variant="primary"
                onClick={() => navigate('/videos/new')}
                className="mt-4"
              >
                <Plus className="h-5 w-5 mr-2" />
                Télécharger votre première vidéo
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Vidéo
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statut
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {myVideos.map((video) => (
                    <tr key={video.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-16 flex-shrink-0 bg-gray-100 rounded overflow-hidden">
                            {video.thumbnail_url ? (
                              <img
                                src={video.thumbnail_url}
                                alt={video.title}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="h-full w-full flex items-center justify-center">
                                <Filter className="h-5 w-5 text-gray-400" />
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {video.title}
                            </div>
                            <div className="text-sm text-gray-500 truncate max-w-xs">
                              {video.description?.substring(0, 50) || 'No description'}
                              {video.description && video.description.length > 50 ? '...' : ''}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(video.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(video.status)}
                        {video.status === 'rejected' && video.rejection_reason && (
                          <div className="mt-1 text-xs text-red-600">
                            Raison: {video.rejection_reason}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/videos/${video.id}`)}
                          >
                            Voir
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => {
                              setVideoToDelete(video.id)
                              setShowDeleteConfirm(true)
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 mb-8">
            <div className="relative flex-1 max-w-lg">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm"
                placeholder="Rechercher des vidéos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className="flex items-center space-x-4">
              <button
                type="button"
                className={`inline-flex items-center px-4 py-2 border rounded-md text-sm font-medium ${
                  sortBy === 'recent'
                    ? 'bg-primary text-white border-transparent'
                    : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                }`}
                onClick={() => setSortBy('recent')}
              >
                Récentes
              </button>
              <button
                type="button"
                className={`inline-flex items-center px-4 py-2 border rounded-md text-sm font-medium ${
                  sortBy === 'popular'
                    ? 'bg-primary text-white border-transparent'
                    : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                }`}
                onClick={() => setSortBy('popular')}
              >
                <TrendingUp className="h-5 w-5 mr-2" />
                Populaires
              </button>
            </div>
          </div>

          <VideoGrid searchQuery={searchQuery} sortBy={sortBy} />
        </>
      )}
    </div>
  )
}